/**
 * Fragmentation Impact Section - Where Margin Quietly Dies
 *
 * Detailed explanation of how fragmented processes and lack of data insights
 * prevent profitability past a certain point
 * Background: White section with dark text for readability
 */

import { SectionHeading, Muted } from "@/components/ui/section-heading";

const FragmentationImpact = () => {
  return (
    <section className="relative bg-white/90 py-16 sm:py-24 lg:py-32 overflow-hidden shadow-2xl rounded-3xl">
      {/* SUBTLE BACKGROUND ACCENT */}
      <div
        className="absolute top-0 right-0 w-1/3 h-full opacity-5"
        style={{
          backgroundImage: `linear-gradient(135deg, #B87333 0%, transparent 70%)`,
        }}
      />

      {/* CONTENT CONTAINER */}
      <div className="relative z-10 max-w-[1600px] mx-auto px-4 sm:px-6">
        {/* SECTION HEADER */}
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <SectionHeading
            variant="question"
            className="mb-4 sm:mb-6 text-center"
          >
            Where Margin Quietly Dies
          </SectionHeading>
          <SectionHeading variant="headline" className="text-center">
            Fragmented processes make work multiply
            <br />
            <Muted>without creating value</Muted>
          </SectionHeading>
        </div>

        {/* TWO-COLUMN: Content Left, Image Right */}
        <div className="bg-gradient-to-br from-slate-50/90 to-white/90 rounded-3xl shadow-xl border-2 border-slate-100 overflow-hidden">
          <div className="grid md:grid-cols-2 gap-0">
            {/* Left Column - Summary Content */}
            <div className="order-2 md:order-1 p-8 md:p-12 flex items-center">
              <div>
                <p className="text-lg text-slate-700 font-semibold leading-relaxed">
                  In most growing companies, each function adopts its own tools
                  over time. Sales lives in a CRM, marketing in ad platforms and
                  analytics tools, operations in inventory or project software,
                  finance in accounting systems, and leadership in spreadsheets.
                  None of these systems were designed together—
                  <strong className="text-slate-900">
                    they don't share context, definitions, or timing
                  </strong>
                  . The same information gets entered multiple times. Teams
                  export CSVs, reconcile mismatched fields, and manually create
                  reports that reflect what happened{" "}
                  <strong className="text-slate-900">weeks ago</strong>, not
                  what's happening now. High-paid talent spends hours
                  coordinating between systems rather than executing work that
                  drives revenue. Context switching between 5–6 tools per day
                  destroys focus and increases burnout. At scale, this
                  compounds: every new SKU, channel, or hire adds more handoffs
                  and more systems to reconcile. Revenue can keep climbing, but{" "}
                  <strong className="text-red-700">
                    margins stall because the operating model is fundamentally
                    inefficient
                  </strong>
                  . You're scaling <strong>activity</strong>, not{" "}
                  <strong>leverage</strong>.
                </p>
              </div>
            </div>

            {/* Right Column - Image (shown first on mobile) */}
            <div className="order-1 md:order-2 relative bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center">
              <img
                src="/margin_erosion.png"
                alt="Margin erosion visualization - how fragmented processes silently destroy profitability"
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FragmentationImpact;
